import { getAllEmailTemplateNames, type GetAllEmailTemplateNamesResponse } from '@repo/email';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { OwnerSelectDropdown } from '@repo/ui';
import { translate, TranslationEntity, useDebounce } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Form, message, Row, Select,
  Tooltip,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { allowedEmailToOptions } from 'automations/data/actionNodes';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import {
  type SendEmailNodeData,
  type SendEmailNodeType,
} from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import { getErrorList } from 'automations/utils/zod';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import styles from './SendEmailContent.module.scss';

type SendEmail = SendEmailNodeData['additionalOptions']['sendEmail'];

const DEBOUNCE_DELAY = 500;

export default function SendEmailContent({
  node,
}: {
  node: NodeProps<SendEmailNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const { open, setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, DEBOUNCE_DELAY);
  const [nodeData, setNodeData] = useState<Partial<SendEmail>>(
    node.data?.additionalOptions?.sendEmail,
  );
  const parentId = useSelector(getParentIdSelector(node.id));

  const {
    data: emailTemplateNames,
    isLoading: isLoadingEmailTemplateNames,
  } = useGetGraphqlDataQuery<UseQueryResult<GetAllEmailTemplateNamesResponse>>({
    document: getAllEmailTemplateNames,
    variables: {
      templateTypes: ['system'],
      searchText: debouncedSearchText,
    },
  }, {
    skip: !open,
  });

  const errors = useMemo(() => {
    const errorList = getErrorList(nodeSchemas.SendEmailActionNodeData, {
      ...node.data,
      additionalOptions: {
        ...node.data?.additionalOptions,
        sendEmail: nodeData,
      },
    });
    return errorList;
  }, [node.data, nodeData]);

  const handleChange = useCallback((key: keyof SendEmail, value: string | number) => {
    setNodeData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, [setNodeData]);

  const emailTemplateOptions = useMemo(
    () => emailTemplateNames?.emailTemplates?.emailTemplates?.map((template) => ({
      label: template.name,
      value: template.id,
    })) ?? [],
    [emailTemplateNames],
  );

  const handleSave = useCallback(async () => {
    if (errors.length > 0) {
      message.error('Please fill in all the fields');
      return;
    }
    setLoading(true);

    try {
      const parsedData = nodeSchemas.SendEmailActionNodeData.parse({
        ...node.data,
        additionalOptions: {
          ...node.data?.additionalOptions,
          sendEmail: nodeData,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedData.additionalOptions,
      }));

      await dispatch(saveActionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? 'Fill all the required fields'
        : error?.data?.message ?? 'Something went wrong';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [contentMode, dispatch, errors.length, node.data, node.id, nodeData, parentId, setMenuOpen]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: 'Action: Send Email',
        destroyOnClose: true,
      }}
      extraLarge={false}
    >
      <Card title="Email Content">
        <Row>
          <Col span={6}>
            <Form.Item
              label="Send Email To:"
              colon={false}
            />
          </Col>
          <Col span={12}>
            <Select
              options={allowedEmailToOptions}
              className={styles.select}
              value={nodeData?.to}
              onChange={(value) => handleChange('to', value)}
              placeholder="Select the recipient of the email"
            />
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Form.Item
              label="Send the email from:"
              colon={false}
            />
          </Col>
          <Col span={12}>
            <OwnerSelectDropdown
              value={nodeData?.from}
              handleClick={(value) => handleChange('from', value as string)}
              selectProps={{
                placeholder: 'Select the sender of the email',
                className: styles.select,
              }}
            />
          </Col>
        </Row>

        <Row>
          <Col span={6}>
            <Form.Item
              label="Choose an Email Template:"
              colon={false}
              tooltip={translate(
                // eslint-disable-next-line max-len
                `To Create a new email template, Open the Email via #{${TranslationEntity.lead}} Detail and click on Save as Template`,
                [{ capitalize: true }],
              )}
            />
          </Col>
          <Col span={12}>
            <Select
              options={emailTemplateOptions}
              className={styles.select}
              value={nodeData?.templateId?.toString()}
              onChange={(value) => handleChange('templateId', Number(value))}
              placeholder="Select a template, or search to find more"
              loading={isLoadingEmailTemplateNames}
              showSearch
              optionFilterProp="label"
              onSearch={setSearchText}
              filterOption={false}
            />
          </Col>
        </Row>
      </Card>

      <Tooltip title={errors.length > 0 ? `Please fill in all the fields: ${errors.join(', ')}` : ''}>
        <Button
          type="primary"
          disabled={errors.length > 0}
          className={styles.saveBtn}
          loading={loading}
          onClick={handleSave}
        >
          Save Email Content
        </Button>
      </Tooltip>
    </NodeMenu>
  );
}
